import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { SiteHeader } from "@/components/site-header";
import { Button } from "@/components/ui/button";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter,
} from "@/components/ui/sheet";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogClose,
} from "@/components/ui/dialog";

import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  ChevronLeftIcon, ChevronRightIcon, ChevronDownIcon, PlusIcon, AlertTriangleIcon,
  Trash2Icon, PencilIcon, InfoIcon, EyeIcon, TargetIcon, RotateCcwIcon,
} from "lucide-react";
import { authorizedFetch } from "@/lib/api";
import { usePermissions } from "@/hooks/use-permissions";
import { cn } from "@/lib/utils";
import { toast } from "sonner";


const API = import.meta.env.VITE_API_URL ?? "http://localhost:3001";

// ─── Types ────────────────────────────────────────────────────────────────────


interface WeekStats {
  id:            string;
  weekLabel:     string;
  weekStartDate: string;
  weekEndDate:   string;
  targetTn:      number;
  notes:         string | null;
  realTn:        number;
  adjustmentTn:  number;
  totalTn:       number;
  completionPct: number;
  truckCount:    number;
  truckNeeded:   number;
  truckMissing:  number;

}

interface MonthlyStats {
  contract: {
    id:                  string;
    name:                string;
    workLine:            string;
    clientId:            string;
    clientName:          string | null;
    clientCode:          string | null;
    monthlyTargetTn:     string;
    refWeightTnPerTruck: string;
    startDate:           string;
    endDate:             string | null;
  };
  monthlyTargetTn:   number;
  hasCustomTarget:   boolean;
  totalTargetTn:     number;
  totalRealTn:       number;

  totalTn:           number;
  completionPct:     number;
  remainingTn:       number;
  remainingTrucks:   number;
  weeks:             WeekStats[];
}

// ─── Helpers (imported from contractDetailUtils for testability) ─────────────
import {
  tn, pct, fmtDate, fmtDateFull,
  computeBarSegments, computeFilledPct, computeRemainingPct,
  WEEK_COLORS, MONTH_NAMES,
} from "./contractDetailUtils";
import {
  DELIVERY_NOTE_STATUS_LABEL, DELIVERY_NOTE_STATUS_CLASS,
  type DeliveryNoteStatus,
} from "../shipments/ShipmentDetailUtils";

// ─── Week Form ────────────────────────────────────────────────────────────────

interface WeekFormState {
  weekLabel:     string;
  weekStartDate: string;
  weekEndDate:   string;
  targetTn:      string;
  notes:         string;
}


// ─── Shipments panel (separate component to allow its own useQuery) ───────────

interface ShipmentRow {
  id:                     string;
  internalId:             number;
  deliveryNoteNumber:     string | null;
  externalId:             string | null;
  originLoadingAt:        string | null;
  destinationUnloadingAt: string | null;
  loadingWeightKg:        string | null;
  unloadingWeightKg:      string | null;
  status:                 string;
  deliveryNoteStatus:     "normal" | "validado" | "reclamado";
  driverName:             string | null;
  truckPlate:             string | null;
  trailerPlate:           string | null;
}

function WeekShipmentsPanel({ contractId, weekId }: { contractId: string; weekId: string }) {
  const { data: rows = [], isLoading } = useQuery<ShipmentRow[]>({
    queryKey: ["week-shipments", weekId],
    queryFn: async () => {
      const res = await authorizedFetch(`${API}/api/relations/contracts/${contractId}/weeks/${weekId}/shipments`);
      return res.json();
    },
  });

  if (isLoading) {
    return <div className="py-3 px-4 text-sm text-muted-foreground">Cargando envíos...</div>;
  }

  if (rows.length === 0) {
    return <div className="py-3 px-4 text-sm text-muted-foreground italic">Sin cargas en este período.</div>;
  }

  const totalValidado  = rows
    .filter(r => r.status !== "cancelado")
    .reduce((s, r) => s + (r.unloadingWeightKg ? Number(r.unloadingWeightKg) / 1000 : 0), 0);
  const cancelledCount = rows.filter(r => r.status === "cancelado").length;

  const remitoBadge = (status: ShipmentRow["deliveryNoteStatus"]) => {
    const map = {
    };
    return <span className={cn("px-1.5 py-0.5 rounded text-[10px] font-semibold", DELIVERY_NOTE_STATUS_CLASS[status])}>{DELIVERY_NOTE_STATUS_LABEL[status]}</span>;
  };

  const SHIPMENT_STATUS_LABEL: Record<string, string> = {
    finalizado: "Finalizado",
    confirmado: "Confirmado",
    en_curso:   "En curso",
    pendiente:  "Pendiente",
    cancelado:  "Cancelado",
  };
  const SHIPMENT_STATUS_CLASS: Record<string, string> = {
    finalizado: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300",
    confirmado: "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300",
    en_curso:   "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
    pendiente:  "bg-muted text-muted-foreground",
    cancelado:  "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  };
  const shipmentBadge = (status: string) => (
    <span className={cn("px-1.5 py-0.5 rounded text-[10px] font-semibold", SHIPMENT_STATUS_CLASS[status] ?? "bg-muted text-muted-foreground")}>
      {SHIPMENT_STATUS_LABEL[status] ?? status}
    </span>
  );

  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-muted-foreground border-b bg-muted/20">
          <th className="text-left px-4 py-2 font-medium">#</th>
          <th className="text-left px-4 py-2 font-medium">Remito</th>
          <th className="text-left px-4 py-2 font-medium">Fecha carga</th>
          <th className="text-left px-4 py-2 font-medium">Chofer / Móvil</th>
          <th className="text-left px-4 py-2 font-medium">Estado carga</th>
          <th className="text-left px-4 py-2 font-medium">Estado remito</th>
          <th className="text-right px-4 py-2 font-medium">TN carg.</th>
          <th className="text-right px-4 py-2 font-medium">TN desc.</th>
          <th className="w-8" />
        </tr>
      </thead>
      <tbody>
        {rows.map(r => {
          const isValidated = r.deliveryNoteStatus === "validado";
          const isCancelled = r.status === "cancelado";
          return (
            <tr key={r.id} className={cn("border-b last:border-0 hover:bg-muted/20", (isCancelled || !isValidated) && "opacity-50")}>
              <td className="px-4 py-1.5 font-mono text-muted-foreground">{r.internalId}</td>
              <td className="px-4 py-1.5 font-mono">{r.deliveryNoteNumber ?? r.externalId ?? "—"}</td>
              <td className="px-4 py-1.5 text-muted-foreground">
                {r.originLoadingAt ? new Date(r.originLoadingAt).toLocaleDateString("es-AR") : "—"}
              </td>
              <td className="px-4 py-1.5">
                {r.driverName
                  ? <div>
                      <div className="font-medium">{r.driverName}</div>
                      {(r.truckPlate || r.trailerPlate) && (
                        <div className="text-muted-foreground font-mono">
                          {[r.truckPlate, r.trailerPlate].filter(Boolean).join(" / ")}
                        </div>
                      )}
                    </div>
                  : <span className="text-muted-foreground">—</span>
                }
              </td>
              <td className="px-4 py-1.5">{shipmentBadge(r.status)}</td>
              <td className="px-4 py-1.5">{remitoBadge(r.deliveryNoteStatus)}</td>
              <td className="px-4 py-1.5 text-right font-mono">
                {r.loadingWeightKg ? (Number(r.loadingWeightKg) / 1000).toLocaleString("es-AR", { minimumFractionDigits: 2 }) : "—"}
              </td>
              <td className="px-4 py-1.5 text-right font-mono font-semibold">
                {r.unloadingWeightKg ? (Number(r.unloadingWeightKg) / 1000).toLocaleString("es-AR", { minimumFractionDigits: 2 }) : "—"}
              </td>
              <td className="px-2 py-1.5">
                <Link to={`/shipments/${r.id}`}>
                  <Button variant="ghost" size="icon" className="size-6" title="Ver carga">
                    <EyeIcon className="size-3.5" />
                  </Button>
                </Link>
              </td>
            </tr>
          );
        })}
      </tbody>
      <tfoot>
        <tr className="border-t text-xs font-semibold bg-muted/10">
          <td colSpan={7} className="px-4 py-1.5 text-muted-foreground">
            {rows.length} carga{rows.length !== 1 ? "s" : ""}
            {cancelledCount > 0 && <span className="ml-1 text-red-600">· {cancelledCount} cancelada{cancelledCount !== 1 ? "s" : ""}</span>}
            <span className="ml-1">· no cuentan las canceladas</span>
          </td>
          <td className="px-4 py-1.5 text-right font-mono text-emerald-600">{totalValidado.toLocaleString("es-AR", { minimumFractionDigits: 2 })}</td>
          <td />
        </tr>
      </tfoot>
    </table>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function ContractDetailPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const { can } = usePermissions();
  const canWrite = can("write", "contracts");

  const now = new Date();
  const [year,  setYear]  = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  // Week sheet
  const [weekSheetOpen,  setWeekSheetOpen]  = useState(false);
  const [editingWeek,    setEditingWeek]    = useState<WeekStats | null>(null);
  const [weekForm,       setWeekForm]       = useState<WeekFormState>({ weekLabel: "", weekStartDate: "", weekEndDate: "", targetTn: "", notes: "" });
  const [weekFormError,  setWeekFormError]  = useState<string | null>(null);
  const [deletingWeek,   setDeletingWeek]   = useState<WeekStats | null>(null);

  // Monthly target edit
  const [targetDialogOpen,  setTargetDialogOpen]  = useState(false);
  const [targetInputValue,  setTargetInputValue]  = useState("");
  const [targetFormError,   setTargetFormError]   = useState<string | null>(null);

  // Expanded rows state
  const [expandedWeeks, setExpandedWeeks] = useState<Set<string>>(new Set());

  function toggleWeek(weekId: string) {
    setExpandedWeeks(s => {
      const next = new Set(s);
      next.has(weekId) ? next.delete(weekId) : next.add(weekId);
      return next;
    });
  }

  const statsKey = ["contract-monthly-stats", id, year, month];

  const { data, isLoading } = useQuery<MonthlyStats>({
    queryKey: statsKey,
    queryFn: async () => {
      const res = await authorizedFetch(`${API}/api/relations/contracts/${id}/monthly-stats?year=${year}&month=${month}`);
      return res.json();
    },
    enabled: !!id,
  });

  // ── Month navigation ────────────────────────────────────────────────────────

  function prevMonth() {
    if (month === 1) { setYear(y => y - 1); setMonth(12); }
    else setMonth(m => m - 1);
  }

  function nextMonth() {
    if (month === 12) { setYear(y => y + 1); setMonth(1); }
    else setMonth(m => m + 1);
  }

  // ── Week mutations ──────────────────────────────────────────────────────────

  function openCreateWeek() {
    setEditingWeek(null);
    const nextNum = (data?.weeks.length ?? 0) + 1;
    setWeekForm({ weekLabel: `Semana ${nextNum}`, weekStartDate: "", weekEndDate: "", targetTn: "", notes: "" });
    setWeekFormError(null);
    setWeekSheetOpen(true);
  }

  function openEditWeek(w: WeekStats) {
    setEditingWeek(w);
    setWeekForm({ weekLabel: w.weekLabel, weekStartDate: w.weekStartDate, weekEndDate: w.weekEndDate, targetTn: String(w.targetTn), notes: w.notes ?? "" });
    setWeekFormError(null);
    setWeekSheetOpen(true);
  }

  const saveWeekMut = useMutation({
    mutationFn: async () => {
      const body = { weekLabel: weekForm.weekLabel, weekStartDate: weekForm.weekStartDate, weekEndDate: weekForm.weekEndDate, targetTn: Number(weekForm.targetTn), notes: weekForm.notes || null };
      if (editingWeek) {
        await authorizedFetch(`${API}/api/relations/contracts/${id}/weeks/${editingWeek.id}`, {
          method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
        });
      } else {
        await authorizedFetch(`${API}/api/relations/contracts/${id}/weeks`, {
          method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
        });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: statsKey });
      setWeekSheetOpen(false);
      setEditingWeek(null);
      toast.success(editingWeek ? "Semana actualizada" : "Semana definida");
    },
    onError: (err: Error) => setWeekFormError(err.message),
  });

  const deleteWeekMut = useMutation({
    mutationFn: async () => {
      await authorizedFetch(`${API}/api/relations/contracts/${id}/weeks/${deletingWeek!.id}`, { method: "DELETE" });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: statsKey });
      setDeletingWeek(null);
      toast.success("Semana eliminada");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // ── Generate weeks ──────────────────────────────────────────────────────────

  const generateWeeksMut = useMutation({
    mutationFn: async () => {
      const res = await authorizedFetch(
        `${API}/api/relations/contracts/${id}/weeks/generate?year=${year}&month=${month}`,
        { method: "POST" },
      );
      return res.json() as Promise<{ created: number; skipped: number }>;
    },
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: statsKey });
      if (result.created === 0) {
        toast.info("Todas las semanas del mes ya estaban definidas");
      } else {
        toast.success(`${result.created} semana${result.created > 1 ? "s" : ""} generada${result.created > 1 ? "s" : ""}${result.skipped > 0 ? `, ${result.skipped} ya existían` : ""}`);
      }
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // ── Monthly target mutations ───────────────────────────────────────────────

  const yearMonth = `${year}-${String(month).padStart(2, "0")}`;

  function openTargetDialog() {
    setTargetInputValue(String(data?.monthlyTargetTn ?? ""));
    setTargetFormError(null);
    setTargetDialogOpen(true);
  }

  const saveTargetMut = useMutation({
    mutationFn: async () => {
      await authorizedFetch(
        `${API}/api/relations/contracts/${id}/monthly-targets/${yearMonth}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ targetTn: Number(targetInputValue) }),
        },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: statsKey });
      setTargetDialogOpen(false);
      toast.success(`Objetivo de ${MONTH_NAMES[month - 1]} actualizado`);
    },
    onError: (err: Error) => setTargetFormError(err.message),
  });

  const resetTargetMut = useMutation({
    mutationFn: async () => {
      await authorizedFetch(
        `${API}/api/relations/contracts/${id}/monthly-targets/${yearMonth}`,
        { method: "DELETE" },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: statsKey });
      toast.success("Objetivo mensual restablecido al valor del contrato");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // ── Render ──────────────────────────────────────────────────────────────────

  const contract = data?.contract;

  const breadcrumb = (
    <span className="text-muted-foreground text-sm">
      <Link to="/relations/contracts" className="hover:underline">Contratos</Link>
      {" / "}
      <span>{contract?.name ?? "Cargando..."}</span>
    </span>
  );

  if (isLoading && !data) {
    return (
      <>
        <SiteHeader title="Contrato" />
        <div className="flex-1 flex items-center justify-center text-muted-foreground">Cargando...</div>
      </>
    );
  }

  const weeks = data?.weeks ?? [];

  const barSegments = computeBarSegments(weeks, data?.monthlyTargetTn ?? 0, [...WEEK_COLORS]);
  const filledPct   = computeFilledPct(data?.totalTn ?? 0, data?.monthlyTargetTn ?? 0);
  const remaining   = computeRemainingPct(filledPct);

  return (
    <>
      <SiteHeader title={contract?.name ?? "Contrato"} />
      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6 gap-6">

        {/* Breadcrumb + meta */}
        <div className="flex flex-col gap-1">
          {breadcrumb}
          <div className="flex flex-wrap items-center justify-between gap-3 mt-1">
            <div>
              <h1 className="text-2xl font-bold tracking-tight">{contract?.clientName} — objetivo mensual y avance semanal</h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                Cada semana se cierra y se guarda como <strong>historial</strong>; su tonelaje cargado suma al <strong>objetivo mensual</strong> del contrato.
              </p>
            </div>
            {/* Month selector */}
            <div className="flex items-center gap-2 border rounded-md px-3 py-1.5 bg-background">
              <Button variant="ghost" size="icon" className="size-6" onClick={prevMonth}>
                <ChevronLeftIcon className="size-4" />
              </Button>
              <span className="text-sm font-medium min-w-28 text-center">
                {MONTH_NAMES[month - 1]} {year}
              </span>
              <Button variant="ghost" size="icon" className="size-6" onClick={nextMonth}>
                <ChevronRightIcon className="size-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="rounded-lg border p-4 space-y-1">
            <div className="flex items-center justify-between gap-1">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Objetivo mensual</div>
              {canWrite && (
                <div className="flex items-center gap-1">
                  {data?.hasCustomTarget && (
                    <button
                      className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-semibold hover:bg-blue-200 transition-colors"
                      title="Objetivo personalizado para este mes. Click para restablecer al valor del contrato."
                      onClick={() => resetTargetMut.mutate()}
                      disabled={resetTargetMut.isPending}
                    >
                      <RotateCcwIcon className="inline size-2.5 mr-0.5" />
                      personalizado
                    </button>
                  )}
                  <button
                    className="text-muted-foreground hover:text-foreground transition-colors"
                    title={`Editar objetivo de ${MONTH_NAMES[month - 1]}`}
                    onClick={openTargetDialog}
                  >
                    <TargetIcon className="size-3.5" />
                  </button>
                </div>
              )}
            </div>
            <div className="text-2xl font-bold font-mono">{tn(data?.monthlyTargetTn ?? 0)} <span className="text-sm font-normal text-muted-foreground">TN</span></div>
            <div className="text-xs text-muted-foreground">
              {data ? tn(data.monthlyTargetTn / 30) : "—"} TN/día · {contract?.startDate ? fmtDateFull(contract.startDate) : "—"}
            </div>
          </div>

          <div className="rounded-lg border p-4 space-y-1">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Cargado acumulado</div>
            <div className="text-2xl font-bold font-mono text-emerald-600">{tn(data?.totalTn ?? 0)}</div>
            <div className="text-xs text-muted-foreground">{weeks.filter(w => w.totalTn > 0).length} semanas · en TN</div>
          </div>

          <div className="rounded-lg border p-4 space-y-1">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Avance mensual</div>
            <div className="text-2xl font-bold">{pct(data?.completionPct ?? 0)}%</div>
            <div className="w-full bg-muted rounded-full h-1.5 mt-1">
              <div className="bg-primary h-1.5 rounded-full transition-all" style={{ width: `${Math.min(100, data?.completionPct ?? 0)}%` }} />
            </div>
          </div>

          <div className="rounded-lg border p-4 space-y-1">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Faltan p/ objetivo</div>
            <div className={cn("text-2xl font-bold font-mono", (data?.remainingTn ?? 0) > 0 ? "text-destructive" : "text-emerald-600")}>
              {tn(data?.remainingTn ?? 0)}
            </div>
            <div className="text-xs text-muted-foreground">≈ {data?.remainingTrucks ?? 0} camiones · TN</div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="rounded-lg border p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="font-medium text-sm">Progreso hacia el objetivo mensual <span className="text-muted-foreground">{tn(data?.monthlyTargetTn ?? 0)} TN</span></div>
            <div className="text-sm font-semibold text-emerald-600">{pct(filledPct)}% cargado</div>
          </div>
          {/* Stacked segments */}
          <div className="flex rounded-md overflow-hidden h-8 w-full gap-px">
            {barSegments.map((seg, i) => (
              <div
                key={i}
                className={cn("flex items-center justify-center text-white text-xs font-medium truncate px-1", seg.color)}
                style={{ width: `${seg.widthPct}%`, minWidth: seg.totalTn > 0 ? "4px" : 0 }}
                title={`${seg.label}: ${tn(seg.totalTn)} / ${tn(seg.targetTn)} TN`}
              >
                {seg.widthPct > 5 ? `S${i + 1}` : ""}
              </div>
            ))}
            {remaining > 0.5 && (
              <div
                className="bg-muted flex items-center justify-center text-muted-foreground text-xs"
                style={{ width: `${remaining}%` }}
              >
                {remaining > 5 ? "Falta" : ""}
              </div>
            )}
          </div>
          {/* Legend */}
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {barSegments.map((seg, i) => (
              <div key={i} className="flex items-center gap-1.5 text-xs">
                <div className={cn("size-2.5 rounded-sm", seg.color)} />
                <span>{seg.label} {tn(seg.totalTn)} TN</span>
              </div>
            ))}
            {remaining > 0.5 && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <div className="size-2.5 rounded-sm bg-muted border" />
                <span>Faltante {tn(data?.remainingTn ?? 0)} TN</span>
              </div>
            )}
          </div>
        </div>

        {/* Weekly table */}
        <div className="rounded-lg border">
          <div className="flex items-center justify-between px-4 py-3 border-b gap-2 flex-wrap">
            <div className="font-medium">Historial semanal <span className="text-muted-foreground text-sm">{weeks.length} semanas</span></div>
            <div className="flex items-center gap-2">
              {canWrite && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => generateWeeksMut.mutate()}
                    disabled={generateWeeksMut.isPending}
                    title={`Genera todas las semanas de ${MONTH_NAMES[month - 1]} dividiendo el objetivo mensual${data?.hasCustomTarget ? " personalizado" : ""} en partes iguales. No pisa semanas ya definidas.`}
                  >
                    {generateWeeksMut.isPending ? "Generando..." : "Generar semanas"}
                  </Button>
                  <Button size="sm" onClick={openCreateWeek}>
                    <PlusIcon className="size-3.5 mr-1" />
                    Definir semana
                  </Button>
                </>
              )}
            </div>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="text-xs">
                  <TableHead className="w-8" />
                  <TableHead>Semana</TableHead>
                  <TableHead>Período</TableHead>
                  <TableHead className="text-right">TN a cumplir</TableHead>
                  <TableHead className="text-right">TN cargadas</TableHead>
                  <TableHead>% cumplido</TableHead>
                  <TableHead className="hidden md:table-cell">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger className="flex items-center gap-1 cursor-default">
                          Camiones
                          <InfoIcon className="size-3 text-muted-foreground" />
                        </TooltipTrigger>
                        <TooltipContent side="top" className="w-52 text-xs bg-muted text-foreground border shadow-sm">
                          <div className="space-y-0.5">
                            <div className="font-semibold">Camiones necesarios</div>
                            <div>Ref.: <strong>{Number(contract?.refWeightTnPerTruck ?? 34).toLocaleString("es-AR", { maximumFractionDigits: 1 })} TN/camión</strong> — definido en el contrato.</div>
                            <div className="text-muted-foreground">Necesarios = ⌈ objetivo ÷ ref. ⌉</div>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableHead>
                  <TableHead className="text-right">TN faltan</TableHead>
                  <TableHead className="w-20" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {weeks.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center text-muted-foreground py-10">
                      No hay semanas definidas para este mes.
                      {canWrite && (
                        <Button variant="link" className="ml-1 h-auto p-0" onClick={openCreateWeek}>Definir primera semana</Button>
                      )}
                    </TableCell>
                  </TableRow>
                )}
                {weeks.map((week, i) => {
                  const isExpanded = expandedWeeks.has(week.id);
                  const diff = week.totalTn - week.targetTn;
                  const isLow = week.completionPct < 80 && week.targetTn > 0;
                  const color = WEEK_COLORS[i % WEEK_COLORS.length];

                  return (
                    <>
                      <TableRow key={week.id} className={cn("hover:bg-muted/50", isExpanded && "bg-muted/30")}>
                        {/* Color dot */}
                        <TableCell>
                          <div className={cn("size-3 rounded-full mx-auto", color)} />
                        </TableCell>
                        {/* Label */}
                        <TableCell className="font-medium">
                          <span>{week.weekLabel}</span>
                        </TableCell>
                        {/* Period */}
                        <TableCell className="text-sm text-muted-foreground">
                          {fmtDate(week.weekStartDate)} → {fmtDate(week.weekEndDate)}
                        </TableCell>
                        {/* TN a cumplir */}
                        <TableCell className="text-right font-mono text-sm">{tn(week.targetTn)}</TableCell>
                        {/* TN cargadas */}
                        <TableCell className="text-right font-mono text-sm">{tn(week.totalTn)}</TableCell>
                        {/* % cumplido */}
                        <TableCell>
                          <div className="flex items-center gap-2 min-w-28">
                            <span className={cn("font-semibold text-sm", isLow ? "text-amber-600" : "text-foreground")}>
                              {pct(week.completionPct)}%
                            </span>
                            <div className="flex-1 bg-muted rounded-full h-1.5">
                              <div
                                className={cn("h-1.5 rounded-full", isLow ? "bg-amber-500" : "bg-emerald-500")}
                                style={{ width: `${Math.min(100, week.completionPct)}%`, minWidth: week.totalTn > 0 ? "3px" : 0 }}
                              />
                            </div>
                          </div>
                          {isLow && (
                            <div className="flex items-center gap-1 text-amber-600 text-[10px] mt-0.5">
                              <AlertTriangleIcon className="size-3" />
                              <span>Bajo objetivo</span>
                            </div>
                          )}
                        </TableCell>
                        {/* Camiones */}
                        <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                          {week.truckNeeded} nec · {week.truckCount} carg · {week.truckMissing} faltan
                        </TableCell>
                        {/* TN faltan */}
                        <TableCell className={cn("text-right font-mono text-sm", diff < 0 ? "text-destructive" : "text-emerald-600")}>
                          {diff >= 0 ? "+" : ""}{tn(diff)}
                        </TableCell>
                        {/* Actions */}
                        <TableCell>
                          <div className="flex items-center gap-0.5 justify-end">
                            {canWrite && (
                              <>
                                <Button variant="ghost" size="icon" className="size-7" title="Editar semana" onClick={() => openEditWeek(week)}>
                                  <PencilIcon className="size-3.5" />
                                </Button>
                                <Button variant="ghost" size="icon" className="size-7 text-destructive" title="Eliminar semana" onClick={() => setDeletingWeek(week)}>
                                  <Trash2Icon className="size-3.5" />
                                </Button>
                              </>
                            )}
                            <Button
                              variant="ghost" size="icon" className="size-7"
                              onClick={() => toggleWeek(week.id)}
                              title={isExpanded ? "Colapsar detalle" : "Ver cargas"}
                            >
                              {isExpanded
                                ? <ChevronDownIcon className="size-4" />
                                : <ChevronRightIcon className="size-4" />
                              }
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>

                      {/* Combined expanded panel: shipments + events */}
                      {isExpanded && (
                        <TableRow className="bg-muted/30 hover:bg-muted/30">
                          <TableCell colSpan={9} className="p-0">
                            <div className="px-6 py-4 space-y-4">

                              {/* Cargas finalizadas */}
                              <div className="rounded-md border overflow-hidden">
                                <div className="px-4 py-2 border-b bg-background flex items-center gap-2">
                                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Cargas del período</span>
                                </div>
                                <WeekShipmentsPanel contractId={id!} weekId={week.id} />
                              </div>

                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Monthly totals row */}
          {weeks.length > 0 && (
            <div className="border-t px-4 py-3 flex flex-wrap gap-4 text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">Totales del mes</span>
              <span>TN objetivo: <strong>{tn(data?.totalTargetTn ?? 0)}</strong></span>
              <span>TN cargadas: <strong>{tn(data?.totalTn ?? 0)}</strong></span>
              <span>Cumplimiento: <strong>{pct(data?.completionPct ?? 0)}%</strong></span>
            </div>
          )}
        </div>
      </div>

      {/* Week Sheet */}
      <Sheet open={weekSheetOpen} onOpenChange={setWeekSheetOpen}>
        <SheetContent side="right" className="sm:max-w-sm flex flex-col">
          <SheetHeader>
            <SheetTitle>{editingWeek ? "Editar semana" : "Definir semana"}</SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="wk-label">Etiqueta <span className="text-destructive">*</span></Label>
              <Input id="wk-label" value={weekForm.weekLabel} onChange={e => setWeekForm(f => ({ ...f, weekLabel: e.target.value }))} placeholder="Ej: Semana 1" maxLength={50} required />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="wk-start">Inicio (lunes) <span className="text-destructive">*</span></Label>
                <Input id="wk-start" type="date" value={weekForm.weekStartDate} onChange={e => setWeekForm(f => ({ ...f, weekStartDate: e.target.value }))} required />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="wk-end">Fin (domingo) <span className="text-destructive">*</span></Label>
                <Input id="wk-end" type="date" value={weekForm.weekEndDate} onChange={e => setWeekForm(f => ({ ...f, weekEndDate: e.target.value }))} required />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="wk-target">TN objetivo <span className="text-destructive">*</span></Label>
              <Input id="wk-target" type="number" min="0" step="0.01" value={weekForm.targetTn} onChange={e => setWeekForm(f => ({ ...f, targetTn: e.target.value }))} required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="wk-notes">Notas</Label>
              <Input id="wk-notes" value={weekForm.notes} onChange={e => setWeekForm(f => ({ ...f, notes: e.target.value }))} placeholder="Opcional" />
            </div>
            {weekFormError && <p className="text-sm text-destructive">{weekFormError}</p>}
          </div>
          <SheetFooter>
            <Button variant="outline" onClick={() => setWeekSheetOpen(false)} disabled={saveWeekMut.isPending}>Cancelar</Button>
            <Button onClick={() => saveWeekMut.mutate()} disabled={saveWeekMut.isPending || !weekForm.weekLabel || !weekForm.weekStartDate || !weekForm.weekEndDate || !weekForm.targetTn}>
              {saveWeekMut.isPending ? "Guardando..." : "Guardar"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Delete week dialog */}
      <Dialog open={!!deletingWeek} onOpenChange={open => { if (!open) setDeletingWeek(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar semana</DialogTitle>
            <DialogDescription>
              ¿Eliminar <strong>{deletingWeek?.weekLabel}</strong>? Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose><Button variant="outline">Cancelar</Button></DialogClose>
            <Button variant="destructive" onClick={() => deleteWeekMut.mutate()} disabled={deleteWeekMut.isPending}>
              {deleteWeekMut.isPending ? "Eliminando..." : "Eliminar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Monthly target dialog */}
      <Dialog open={targetDialogOpen} onOpenChange={setTargetDialogOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Objetivo mensual — {MONTH_NAMES[month - 1]} {year}</DialogTitle>
            <DialogDescription>
              Define un objetivo específico para este mes. Si no se establece, se usará el valor del contrato
              ({tn(Number(data?.contract?.monthlyTargetTn ?? 0))} TN).
            </DialogDescription>
          </DialogHeader>
          <div className="px-1 py-2 space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="mt-target">Objetivo (TN) <span className="text-destructive">*</span></Label>
              <Input
                id="mt-target"
                type="number"
                min="0"
                step="0.01"
                value={targetInputValue}
                onChange={e => setTargetInputValue(e.target.value)}
                placeholder={String(data?.contract?.monthlyTargetTn ?? "")}
                required
                autoFocus
              />
            </div>
            {targetFormError && <p className="text-sm text-destructive">{targetFormError}</p>}
          </div>
          <DialogFooter>
            <DialogClose><Button variant="outline" disabled={saveTargetMut.isPending}>Cancelar</Button></DialogClose>
            <Button
              onClick={() => saveTargetMut.mutate()}
              disabled={saveTargetMut.isPending || !targetInputValue || Number(targetInputValue) <= 0}
            >
              {saveTargetMut.isPending ? "Guardando..." : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
